// Sightmap document → the `.sightmap/` YAML files a project drops into its repo.
//
// The YAML is written here rather than with a library because the value space is closed (strings,
// string lists, nested maps) and the emitter is then a pure function with no dependency — which is
// what lets the whole thing be tested offline in `npm run test:sightmap`.
//
// Quoting is conservative: a scalar is emitted plain only when it cannot be read as anything else, and
// everything else is double-quoted with JSON escaping. Selectors are the reason — `a[href="/x"]`,
// `.card:first-child` and `#main` are all ambiguous or illegal as plain YAML scalars.
import { SIGHTMAP_VERSION, type SightmapDoc, type SightmapComponent, type SightmapRequest, type SightmapView, type SightmapStructure } from './schema.ts'

type YamlValue = string | number | string[] | YamlMap | YamlValue[]
interface YamlMap { [key: string]: YamlValue | undefined }

// Plain only for a scalar that cannot be read as anything else: it starts with a letter or `/` (so a
// route stays readable) and holds nothing an indicator character could claim. `: ` is excluded because
// that is the one sequence that would split a value into a nested mapping.
const PLAIN_SCALAR = /^[A-Za-z/][\w .\-/:*]*$/
const RESERVED = new Set(['true', 'false', 'null', 'yes', 'no', 'on', 'off'])

function scalar(value: string): string {
  const plain = PLAIN_SCALAR.test(value)
    && !RESERVED.has(value.toLowerCase())
    && !value.includes(': ')
    && !value.endsWith(':')
    && !value.endsWith(' ')
  return plain ? value : JSON.stringify(value)
}

// A number is emitted bare — `version: 1`, not `version: "1"`, which is what the spec's own files hold.
function scalarOf(value: string | number): string {
  return typeof value === 'number' ? String(value) : scalar(value)
}

function emitValue(value: YamlValue, indent: string, lines: string[]): void {
  if (typeof value === 'string' || typeof value === 'number') {
    throw new Error('emitValue expects a container; scalars are written inline')
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'string' || typeof item === 'number') {
        lines.push(`${indent}- ${scalarOf(item)}`)
        continue
      }
      // A map/array inside a list: open with the dash, then indent the rest to line up under it.
      const nested: string[] = []
      emitValue(item, `${indent}  `, nested)
      const first = nested.shift()
      if (first === undefined) continue
      lines.push(`${indent}- ${first.slice(indent.length + 2)}`)
      lines.push(...nested)
    }
    return
  }
  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined) continue
    if (typeof entry === 'string' || typeof entry === 'number') {
      lines.push(`${indent}${key}: ${scalarOf(entry)}`)
      continue
    }
    if (Array.isArray(entry) && entry.length === 0) continue
    lines.push(`${indent}${key}:`)
    emitValue(entry, `${indent}  `, lines)
  }
}

function emitDocument(map: YamlMap): string {
  const lines: string[] = []
  emitValue(map, '', lines)
  return lines.length ? `${lines.join('\n')}\n` : ''
}

function structureMap(structure: SightmapStructure): YamlMap {
  return { tag: structure.tag, type: structure.type, role: structure.role, children: structure.children?.map(structureMap) }
}

function componentMap(component: SightmapComponent): YamlMap {
  return {
    id: component.id,
    name: component.name,
    kind: component.kind,
    actions: component.actions,
    selector: Array.isArray(component.selector) && component.selector.length === 1
      ? component.selector[0]
      : component.selector,
    structure: component.structure ? structureMap(component.structure) : undefined,
    aliases: component.aliases,
    params: component.params,
    memory: component.memory,
    properties: component.properties?.map((p) => ({ name: p.name, extract: p.extract, description: p.description })),
    children: component.children?.map(componentMap),
  }
}

function requestMap(request: SightmapRequest): YamlMap {
  return {
    name: request.name,
    route: request.route,
    method: request.method,
    description: request.description,
    headers: request.headers,
    request: request.request ? { fields: request.request.fields.map((f) => ({ name: f.name, type: f.type, description: f.description })) } : undefined,
    response: request.response ? { fields: request.response.fields.map((f) => ({ name: f.name, type: f.type, description: f.description })) } : undefined,
  }
}

function viewMap(view: SightmapView): YamlMap {
  return {
    id: view.id,
    name: view.name,
    route: view.route,
    description: view.description,
    memory: view.memory,
    components: view.components?.map(componentMap),
    requests: view.requests?.map(requestMap),
  }
}

/** Filename-safe slug for a view name: `Order Detail` → `order-detail`. */
export function slugify(name: string): string {
  const slug = name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'view'
}

export interface SightmapFile {
  /** Repo-relative path, e.g. `.sightmap/order-detail.yaml`. */
  path: string
  contents: string
}

/**
 * The document as the files a repo would hold: one per view (the Atlas convention), plus `shared.yaml`
 * for the app-wide components and requests. The loader merges every `*.yaml` under `.sightmap/`, so the
 * split is presentational — `sightmapYaml` below produces the same content as one document.
 */
export function emitSightmapFiles(doc: SightmapDoc): SightmapFile[] {
  // Every file declares the spec version, and the bundle carries a config.yaml that declares nothing
  // else — which is what the Atlas's own entries hold. The loader merges every *.yaml under .sightmap/,
  // so a version-only file is how the bundle states its version independently of any one view.
  const files: SightmapFile[] = [{ path: '.sightmap/config.yaml', contents: `version: ${SIGHTMAP_VERSION}\n` }]
  const shared = emitDocument({
    components: doc.components?.map(componentMap),
    requests: doc.requests?.map(requestMap),
  })
  if (shared) files.push({ path: '.sightmap/shared.yaml', contents: shared })

  const used = new Set<string>()
  for (const view of doc.views) {
    let slug = slugify(view.name)
    let n = 2
    while (used.has(slug)) slug = `${slugify(view.name)}-${n++}`
    used.add(slug)
    files.push({
      path: `.sightmap/${slug}.yaml`,
      contents: emitDocument({ version: SIGHTMAP_VERSION, views: [viewMap(view)] }),
    })
  }
  return files
}

/** The whole document as one YAML text — what the API returns and the UI previews. */
export function sightmapYaml(doc: SightmapDoc): string {
  return emitDocument({
    version: SIGHTMAP_VERSION,
    views: doc.views.map(viewMap),
    components: doc.components?.map(componentMap),
    requests: doc.requests?.map(requestMap),
  })
}
